import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Configure axios defaults
axios.defaults.withCredentials = true;

// ============================================================
// Global axios 401 interceptor with auto-refresh
// ------------------------------------------------------------
// When any API call returns 401 (access_token expired), we try
// once to call /auth/refresh (uses the 7-day refresh_token cookie
// to mint a new access_token) and replay the original request.
// If the refresh fails, we redirect to /login. This avoids the
// "Not authenticated" toast loop the user was hitting on the
// Clients page after long sessions.
// ============================================================
let isRefreshing = false;
let pendingRequests = [];

const processQueue = (error) => {
  pendingRequests.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve();
  });
  pendingRequests = [];
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    // Only attempt refresh on 401 and never on the auth endpoints themselves
    const url = originalRequest?.url || '';
    const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/refresh') || url.includes('/auth/me') || url.includes('/auth/logout');

    if (status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        // Queue the request until the in-flight refresh resolves
        return new Promise((resolve, reject) => {
          pendingRequests.push({ resolve, reject });
        }).then(() => axios(originalRequest));
      }

      originalRequest._retry = true;
      isRefreshing = true;
      try {
        await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        processQueue(null);
        return axios(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        // Refresh failed — bounce to login (preserve current path so user lands back here)
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
          const next = encodeURIComponent(window.location.pathname + window.location.search);
          window.location.replace(`/login?next=${next}`);
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  },
);

function translateErrorDetail(detail) {
  // Translate known backend error messages to French for the UI
  const map = {
    "Invalid credentials": "Email ou mot de passe incorrect",
    "Not authenticated": "Session expirée. Veuillez vous reconnecter.",
    "User not found": "Aucun compte avec cet email",
    "Email already registered": "Cet email est déjà utilisé",
    "Account is locked": "Compte bloqué temporairement. Réessayez plus tard.",
  };
  if (typeof detail === "string" && map[detail]) return map[detail];
  return detail;
}

function formatApiErrorDetail(detail) {
  if (detail == null) return "Une erreur s'est produite. Veuillez réessayer.";
  if (typeof detail === "string") return translateErrorDetail(detail);
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const checkAuth = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch (e) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(data);
      return { success: true };
    } catch (e) {
      // Most useful error path: server returned a JSON body with a 'detail' field
      const detail = e.response?.data?.detail;
      // If we have a translatable detail, use it
      if (detail) {
        return { success: false, error: formatApiErrorDetail(detail) };
      }
      // If we got a response but no body → use status code to give actionable info
      if (e.response?.status === 401) {
        return { success: false, error: "Email ou mot de passe incorrect" };
      }
      if (e.response?.status === 429) {
        return { success: false, error: "Trop de tentatives. Patientez quelques minutes." };
      }
      if (e.response?.status >= 500) {
        return { success: false, error: `Erreur serveur (${e.response.status}). Réessayez dans une minute.` };
      }
      // No response at all = network/CORS/cookies blocked
      return {
        success: false,
        error: e.message?.includes('Network')
          ? "Impossible de joindre le serveur (réseau ou cookies tiers bloqués)."
          : (e.message || "Une erreur s'est produite. Veuillez réessayer."),
      };
    }
  };

  const register = async (name, email, password) => {
    try {
      const { data } = await axios.post(
        `${API}/auth/register`,
        { name, email, password, role: 'consultant' },
        { withCredentials: true }
      );
      setUser(data);
      return { success: true, user: data };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      console.error('Logout error:', e);
    } finally {
      setUser(false);
    }
  };

  const contextValue = useMemo(() => ({ user, loading, login, register, logout, checkAuth }), [user, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
